import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Box, Paper, Typography, TextField, Button, Link, CircularProgress, Alert, AlertTitle, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { UploadFile, CheckCircleOutline, ErrorOutline } from '@mui/icons-material';

// Asset URLs
const tumLogoUrl = '/logo-sebis.png';
const sylvensteinLogoUrl = '/logo-sylvenstein.png';
const bfsLogoUrl = '/logo-bfs.png';

// --- NEU: Hardcodierte Fehlermeldungen basierend auf den Backend-Codes ---
const ERROR_MESSAGES = {
    'NOT_A_CONTRACT': 'Das hochgeladene Dokument wurde nicht als Arbeitsvertrag erkannt. Bitte laden Sie nur Arbeitsverträge hoch.',
    'POOR_SCAN_QUALITY': 'Die Qualität des Scans ist leider nicht ausreichend. Der Text ist schwer lesbar, verschwommen oder zu dunkel. Bitte laden Sie eine bessere Version hoch.',
    'MULTI_COLUMN_LAYOUT': 'Das Dokument verwendet ein zweispaltiges Layout (z. B. Deutsch/Englisch nebeneinander). Unser System unterstützt derzeit nur einspaltige Standard-Layouts.',
    'COMPLEX_ELEMENTS': 'Das Dokument enthält zu viele komplexe Grafiken, Tabellen oder Bilder, die eine zuverlässige Analyse verhindern.',
    'DEFAULT': 'Das Dokument entspricht leider nicht den Anforderungen für die automatische Analyse.'
};

const LandingPage = () => {
    const [file, setFile] = useState(null);
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [uploadStatus, setUploadStatus] = useState({
        success: false,
        error: null,
        errorTitle: '',
        sentTo: ''
    });

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
            setUploadStatus({ success: false, error: null, errorTitle: '', sentTo: '' });
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        multiple: false,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file || !email) return;

        setIsLoading(true);
        setUploadStatus({ success: false, error: null, errorTitle: '', sentTo: '' });

        const formData = new FormData();
        formData.append('file', file);
        formData.append('email', email);

        try {
            await axios.post('/open/uploadNewContract', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Erfolg
            setUploadStatus({ success: true, error: null, errorTitle: '', sentTo: email });
            setFile(null);
            setEmail('');

        } catch (error) {
            let errorMessage = ERROR_MESSAGES['DEFAULT'];
            let errorTitle = 'Upload fehlgeschlagen';

            if (error.response) {
                const { status, data } = error.response;

                if (status === 422) {
                    // --- ÄNDERUNG: Mapping anhand des Codes ---
                    errorTitle = 'Dokument abgelehnt';
                    const backendCode = data.code; // z.B. "MULTI_COLUMN_LAYOUT"

                    // Wähle den passenden Text oder den Fallback
                    errorMessage = ERROR_MESSAGES[backendCode] || ERROR_MESSAGES['DEFAULT'];

                } else if (status === 400) {
                    errorTitle = 'Ungültige Eingabe';
                    errorMessage = 'Bitte überprüfen Sie, ob Sie eine gültige PDF-Datei ausgewählt haben.';
                } else if (status === 503) {
                    errorTitle = 'Dienst nicht verfügbar';
                    errorMessage = 'Der Analyse-Service ist momentan ausgelastet. Bitte versuchen Sie es in ein paar Minuten erneut.';
                } else if (status === 413) {
                    errorTitle = 'Datei zu groß';
                    errorMessage = 'Die hochgeladene Datei überschreitet das maximale Limit.';
                }
            } else if (error.message) {
                errorMessage = 'Es gab ein Netzwerkproblem. Bitte prüfen Sie Ihre Verbindung.';
            }

            setUploadStatus({ success: false, error: errorMessage, errorTitle: errorTitle, sentTo: '' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Arial, sans-serif',
            overflowX: 'hidden',
        }}>
            {/* Main Content */}
            <Box component="main" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, mt: { xs: 4, md: 0 }, pb: 0 }}>
                <Grid container spacing={{xs: 4, md: 8}} sx={{ maxWidth: '1200px' }} alignItems="center">

                    {/* Left Column (Text) */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
                            KI-gestützte Prüfung Ihres Arbeitsvertrags.
                        </Typography>
                        <Typography variant="h6" color="text.secondary" paragraph>
                            Laden Sie Ihren Arbeitsvertrag hoch und erhalten Sie eine kostenlose, KI-basierte Erstanalyse, gefolgt von einer Prüfung durch spezialisierte Fachanwälte.
                        </Typography>
                        <List>
                            <ListItem disablePadding>
                                <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                <ListItemText primary="Schnelle Analyse auf unwirksame Klauseln" />
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                <ListItemText primary="Anonymisiert für Forschungszwecke" />
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                <ListItemText primary="100% kostenlos" />
                            </ListItem>
                        </List>
                    </Grid>

                    {/* Right Column (Upload Form) */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={4} sx={{ p: { xs: 3, md: 4 }, borderRadius: '12px', border: '1px solid #dee2e6' }}>
                            <Typography variant="h5" component="h2" align="center" fontWeight="bold" gutterBottom>
                                Jetzt Vertrag hochladen
                            </Typography>
                            <form onSubmit={handleSubmit}>
                                <Box
                                    {...getRootProps()}
                                    sx={{
                                        border: `2px dashed ${isDragActive ? '#0065BD' : '#ced4da'}`,
                                        borderRadius: '8px', p: 4, textAlign: 'center', cursor: 'pointer',
                                        backgroundColor: isDragActive ? '#e7f0f7' : '#f8f9fa',
                                        transition: 'background-color 0.2s, border-color 0.2s', mb: 2
                                    }}
                                >
                                    <input {...getInputProps()} />
                                    <UploadFile sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                    {file ? (
                                        <Typography sx={{ color: 'success.main', fontWeight: 'medium' }}>{file.name}</Typography>
                                    ) : (
                                        <Typography color="text.secondary">PDF hier ablegen oder klicken</Typography>
                                    )}
                                </Box>

                                <TextField
                                    label="Ihre E-Mail-Adresse" type="email" required fullWidth
                                    margin="normal" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }}
                                />

                                {/* SUCCESS MESSAGE */}
                                {uploadStatus.success && (
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        <AlertTitle>Upload erfolgreich!</AlertTitle>
                                        Eine Bestätigung wurde an <strong>{uploadStatus.sentTo}</strong> gesendet. Bitte überprüfen Sie Ihr Postfach (auch Spam).
                                    </Alert>
                                )}

                                {/* ERROR MESSAGE */}
                                {uploadStatus.error && (
                                    <Alert severity="error" icon={<ErrorOutline fontSize="inherit" />} sx={{ mb: 2 }}>
                                        <AlertTitle>{uploadStatus.errorTitle}</AlertTitle>
                                        {uploadStatus.error}
                                    </Alert>
                                )}

                                <Button
                                    type="submit" variant="contained" fullWidth disabled={!file || !email || isLoading}
                                    sx={{
                                        py: 1.5,
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        backgroundColor: '#0065BD',
                                        '&:hover': { backgroundColor: '#005299' }
                                    }}
                                >
                                    {isLoading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CircularProgress size={20} color="inherit" sx={{ mr: 2 }} />
                                            Analysiere Dokument...
                                        </Box>
                                    ) : (
                                        'Analyse anfordern'
                                    )}
                                </Button>
                            </form>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3, textAlign: 'center' }}>
                                Mit der Nutzung dieses Dienstes erklären Sie sich einverstanden, dass eine anonymisierte Version Ihres Vertrags für Forschungszwecke verwendet werden darf.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Footer */}
            <Box component="footer" sx={{ p: 3, width: '100%', mt: 'auto' }}>
                <Box sx={{ maxWidth: '1200px', mx: 'auto', textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '2px', mb: 6 }}>
                        Ein kooperatives Forschungsprojekt von
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', flexWrap: 'wrap', mb: 3, width: '100%' }}>
                        <Link href="https://sylvenstein-law.de/" target="_blank" rel="noopener noreferrer">
                            <img src={sylvensteinLogoUrl} alt="SYLVENSTEIN Logo" style={{ height: '35px', objectFit: 'contain', margin: '10px' }} />
                        </Link>
                        <Link href="https://wwwmatthes.in.tum.de/pages/88bkmvw6y7gx/Prof.-Dr.-Florian-Matthes" target="_blank" rel="noopener noreferrer">
                            <img src={tumLogoUrl} alt="TUM Sebis Logo" style={{ height: '40px', objectFit: 'contain', margin: '10px' }} />
                        </Link>
                        <Link href="https://www.forschungsstiftung.bayern.de/" target="_blank" rel="noopener noreferrer">
                            <img src={bfsLogoUrl} alt="Bayerische Forschungsstiftung Logo" style={{ height: '50px', objectFit: 'contain', margin: '10px' }} />
                        </Link>
                    </Box>
                    <Link href="/impressum" underline="hover" color="text.secondary">Impressum</Link>
                    <Typography variant="body2" component="span" sx={{ mx: 1, color: 'text.secondary' }}>|</Typography>
                    <Link href="/nutzungsbedingungen" underline="hover" color="text.secondary">Nutzungsbedingungen</Link>
                </Box>
            </Box>
        </Box>
    );
};

export default LandingPage;